import { CloseOutlined, EyeOutlined, PrinterOutlined } from '@ant-design/icons';
import { useQuery } from '@apollo/client/react';
import { css } from '@emotion/css';
import { App, Button, Empty, List, Modal, Space, theme } from 'antd';
import { useForm } from 'antd/es/form/Form';
import type React from 'react';
import { useState } from 'react';
import { useAuth } from 'react-oidc-context';
import { PdfPreviewModal } from '../../components/PdfPreviewModal.tsx';
import { getFragmentData } from '../../graphql/generated';
import type { FormFieldValuesInput, FormRenderer_FormFragment, PatientDataInput } from '../../graphql/generated/graphql.ts';
import { useCurrentContextStore } from '../../hooks/store/useCurrentContextStore.ts';
import { printErrorShown, usePrinter } from '../../hooks/usePrinter.ts';
import { PrintButton } from '../../PrintButton.tsx';
import { createFormFieldValues } from '../../utils/form.ts';
import { hasFrontDeskRole } from '../../utils/user.ts';
import { FormRendererFragment } from './FormRenderer.gql';
import { FormRenderer } from './FormRenderer.tsx';
import { DIRECT_FORMS_QUERY } from './Forms.gql';

const { useToken } = theme;

interface FormAction {
  form: FormRenderer_FormFragment;
  action: 'view' | 'print';
  values?: FormFieldValuesInput[];
}

export const Forms: React.FC<{ patientData: PatientDataInput }> = ({ patientData }) => {
  const { currentDoctorId, primaryDoctorId } = useCurrentContextStore();
  const { message } = App.useApp();
  const auth = useAuth();
  const doctorId = hasFrontDeskRole(auth.user) ? primaryDoctorId : currentDoctorId;

  const { data, loading } = useQuery(DIRECT_FORMS_QUERY, {
    variables: {
      doctorId: doctorId,
    },
  });
  const directForms = getFragmentData(FormRendererFragment, data?.directForms);

  const [formRendererAction, setFormRendererAction] = useState<FormAction | null>(null);
  const [objectUrl, setObjectUrl] = useState<string>('');
  const [blob, setBlob] = useState<Blob | null>(null);
  const [pdfLoading, setPdfLoading] = useState(false);

  const step1 = async (action: FormAction) => {
    if (action.form.fields.length) {
      setFormRendererAction(action);
      return;
    }
    await execute({ ...action, values: [] });
  };

  const execute = async (action: FormAction) => {
    if (action.action === 'view') {
      setPdfLoading(true);
    }
    try {
      const response = await fetch(`${window._env_.API_URL}/rest/direct-form`, {
        method: 'POST',
        body: JSON.stringify({
          doctorId: doctorId,
          patientData: patientData,
          formId: action.form.id,
          formFieldValues: createFormFieldValues([action.form], action.values),
        }),
        headers: {
          'Content-Type': 'application/json',
          authorization: `Bearer ${auth.user?.access_token}`,
        },
      });

      if (!response.ok) {
        message.error('Fehler bei der Erstellung des Formulars');
        return;
      }

      const blob = await response.blob();
      if (action.action === 'print') {
        await printForm(blob);
        return;
      }

      setBlob(blob);
      setObjectUrl(URL.createObjectURL(new Blob([blob], { type: 'application/pdf' })));
    } catch (_e) {
      message.error('Fehler bei der Erstellung des Formulars');
    } finally {
      setPdfLoading(false);
    }
  };

  const cleanup = () => {
    if (objectUrl) {
      URL.revokeObjectURL(objectUrl);
    }
    setObjectUrl('');
    setBlob(null);
  };

  const { print } = usePrinter();

  const printForm = async (blob: Blob) => {
    try {
      await print(await blob.arrayBuffer(), 'a4', { title: 'Formular' });
      message.success('Dokument wurde an den Drucker gesendet');
    } catch (e) {
      if (!printErrorShown(e)) {
        message.error('Beim Drucken ist ein Fehler aufgetreten');
      }
    }
  };

  return (
    <>
      <List
        loading={loading}
        size="small"
        itemLayout="horizontal"
        dataSource={directForms ? [...directForms].sort((a, b) => a.name.localeCompare(b.name)) : []}
        locale={{
          emptyText: <Empty description="Es stehen im Moment keine Formulare zur Verfügung" image={Empty.PRESENTED_IMAGE_SIMPLE} />,
        }}
        renderItem={item => (
          <List.Item
            // inline, so it wins over the list's item padding without !important
            style={{ paddingInline: 0 }}
            actions={[
              <PrintButton
                key="print"
                type="link"
                icon={<PrinterOutlined />}
                size="small"
                onClick={() => step1({ form: item, action: 'print' })}
              >
                Drucken
              </PrintButton>,
              <Button key="view" type="link" icon={<EyeOutlined />} size="small" onClick={() => step1({ form: item, action: 'view' })}>
                Anzeigen
              </Button>,
            ]}
          >
            {item.name}
          </List.Item>
        )}
      />
      <PdfPreviewModal
        title="Formular"
        url={objectUrl}
        loading={pdfLoading}
        footer={
          <Space orientation="horizontal">
            <PrintButton type="primary" icon={<PrinterOutlined />} onClick={() => (blob ? printForm(blob) : null)}>
              Drucken
            </PrintButton>
            <Button icon={<CloseOutlined />} onClick={cleanup}>
              Schließen
            </Button>
          </Space>
        }
        onClose={cleanup}
      />
      <FormRendererModal
        forms={formRendererAction ? [formRendererAction.form] : []}
        onCancel={() => setFormRendererAction(null)}
        onOk={values => {
          if (formRendererAction) {
            execute({ ...formRendererAction, values: values });
          }
          setFormRendererAction(null);
        }}
      />
    </>
  );
};

const FormRendererModal: React.FC<{
  forms: FormRenderer_FormFragment[];
  onCancel: () => void;
  // biome-ignore lint/suspicious/noExplicitAny: dynamic form values
  onOk: (values: any) => void;
}> = ({ forms, onCancel, onOk }) => {
  return (
    <Modal title="Bitte Formulardaten ergänzen" width={768} open={!!forms.length} footer={null} onCancel={onCancel} destroyOnHidden>
      {forms && <FormRendererWrapper forms={forms} onCancel={onCancel} onOk={onOk} />}
    </Modal>
  );
};

const FormRendererWrapper: React.FC<{
  forms: FormRenderer_FormFragment[];
  onCancel: () => void;
  // biome-ignore lint/suspicious/noExplicitAny: dynamic form values
  onOk: (values: any) => void;
}> = ({ forms, onCancel, onOk }) => {
  const [formInstance] = useForm();
  const { token } = useToken();

  const submit = async () => {
    try {
      await formInstance.validateFields();
    } catch (_error) {
      return;
    }

    onOk(formInstance.getFieldsValue(true));
  };

  return (
    <>
      <FormRenderer formInstance={formInstance} forms={forms} />
      <Space
        orientation="horizontal"
        className={css`
          width: 100%;
          justify-content: end;
          margin-top: ${token.paddingMD}px;
        `}
      >
        <Button onClick={onCancel}>Abbrechen</Button>
        <Button type="primary" onClick={submit}>
          OK
        </Button>
      </Space>
    </>
  );
};
